import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from './AuthContext';
import { getPricingPreset, updatePricingPreset } from '../utils/supabaseClient';

// Default pricing data structure
const defaultPricingData = {
  // Toggle states for each section
  sections: {
    hardware: true,
    software: true,
    installation: true, 
    deployment: true,
    support: true
  },
  // Hardware costs
  hardware: {
    entry: { price: 370.00 },
    oa: { price: 370.00 },
    waffleMeeting: { price: 149.00 },
    waffleDesk: { price: 149.00 }
  },
  // Software costs (annual)
  software: {
    entry: { price: 550.00 },
    oa: { price: 200.00 },
    waffleMeeting: { price: 96.00 },
    waffleDesk: { price: 30.00 }
  },
  // Installation costs
  installation: {
    entry: { price: 400.00 },
    oa: { price: 400.00 },
    waffleMeeting: { price: 0 },
    waffleDesk: { price: 0 }
  },
  // Deployment costs
  deployment: {
    entry: { price: 60.00 },
    oa: { price: 60.00 },
    waffleMeeting: { price: 10.00 },
    waffleDesk: { price: 10.00 }
  },
  // Support and warranty
  support: {
    entry: { price: 60.00 },
    oa: { price: 60.00 },
    waffleMeeting: { price: 10.00 },
    waffleDesk: { price: 10.00 }
  },
  // Terms
  terms: {
    discount: 0,
    includeWarranty: true,
    warrantyYears: 5,
  }
};

// Create the context
const PricingContext = createContext(null);

// Local storage key for pricing data
const PRICING_LOCAL_STORAGE_KEY = 'floorplan_detector_pricing_data';

// Provider component
export function PricingProvider({ children }) {
  const [pricingData, setPricingData] = useState(defaultPricingData);
  const [presetData, setPresetData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false); // To be updated with actual permission check
  const { user } = useAuth();

  // Load pricing data and presets from database
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Check localStorage for cached data
        const cachedData = localStorage.getItem(PRICING_LOCAL_STORAGE_KEY);
        if (cachedData) {
          try {
            const parsedData = JSON.parse(cachedData);
            console.log('Using pricing data from localStorage');
            setPricingData(parsedData);
            setIsLoading(false);
            return;
          } catch (parseError) {
            console.error('Error parsing localStorage data:', parseError);
            localStorage.removeItem(PRICING_LOCAL_STORAGE_KEY);
          }
        }
        
        // If no cached data, use default pricing
        console.log('No localStorage data found, using defaults');
        setPricingData(defaultPricingData);
        
      } catch (err) {
        console.error('Failed to load pricing data:', err);
        // Fallback to defaults
        setPricingData(defaultPricingData);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Save pricing data to localStorage
  const savePricingToLocalStorage = (newPricingData) => {
    try {
      localStorage.setItem(PRICING_LOCAL_STORAGE_KEY, JSON.stringify(newPricingData));
      setPricingData(newPricingData);
      return true;
    } catch (err) {
      console.error('Failed to save pricing data to localStorage:', err);
      return false;
    }
  };

  // Clear local storage cache
  const clearLocalStorage = () => {
    localStorage.removeItem(PRICING_LOCAL_STORAGE_KEY);
  };

  // Update a specific section of pricing data
  const updatePricingSection = (section, newData) => {
    const updatedData = {
      ...pricingData,
      [section]: newData
    };
    
    setPricingData(updatedData);
    return savePricingToLocalStorage(updatedData);
  };

  // Toggle a section's visibility
  const toggleSection = (section, isEnabled) => {
    const updatedSections = {
      ...pricingData.sections,
      [section]: isEnabled
    };
    
    return updatePricingSection('sections', updatedSections);
  };

  // Update a specific price
  const updatePrice = (section, itemKey, price) => {
    if (!pricingData[section]) return false;
    
    // If price is an empty string, store it as is to allow for empty field editing
    // Otherwise convert to number
    const priceValue = price === '' ? '' : Number(price);
    
    const sectionData = {
      ...pricingData[section],
      [itemKey]: { 
        price: priceValue
      }
    };
    
    return updatePricingSection(section, sectionData);
  };

  // Update discount percentage
  const updateDiscount = (discountPercentage) => {
    const terms = {
      ...pricingData.terms,
      discount: Number(discountPercentage)
    };
    
    return updatePricingSection('terms', terms);
  };

  // Reset to default pricing
  const resetToDefaults = () => {
    localStorage.removeItem(PRICING_LOCAL_STORAGE_KEY);
    setPricingData(defaultPricingData);
    return savePricingToLocalStorage(defaultPricingData);
  };

  const contextValue = {
    pricingData,
    presetData,
    isLoading,
    isAdmin,
    savePricingToLocalStorage,
    clearLocalStorage,
    updatePricingSection,
    toggleSection,
    updatePrice,
    updateDiscount,
    resetToDefaults
  };

  return (
    <PricingContext.Provider value={contextValue}>
      {children}
    </PricingContext.Provider>
  );
}

// Custom hook for using the pricing context
export function usePricing() {
  const context = useContext(PricingContext);
  if (!context) {
    throw new Error('usePricing must be used within a PricingProvider');
  }
  return context;
} 